import { yupResolver } from '@hookform/resolvers/yup';
import { Alert } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { FormItemsOnlyFactory } from '../../../../components/Form/FormFactory';
import { StickyFormButtons } from '../../../../components/Form/StickyFormButtons';
import { Loader } from '../../../../components/Loader/Loader';
import {
  BulkTearsheetGenerationPayload,
  bulkTearsheetGenerationSchema,
} from '../../../../schemas/BulkTearsheetGeneration.schema';
import { generateTearsheets } from '../../../../services/queries/MaggieTearsheetQueries';
import { selectableFundsAtom } from '../../../../services/state/AppConfigStateJ';
import { portfolioCompaniesAtom } from '../../../../services/state/PortCosStateJ';
import { FDMap } from '../../../../util/data-structure/FDMap';
import { trackEvent } from '../../../../util/datadogTracking';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { schemaToFormFields } from '../../../../util/schema-utils';
import { IForm } from '../../../../view-models/form.view-model';

interface IBulkTearsheetGenerationFormProps {
  onClose: () => void;
  onRefresh?: () => void;
}

function BulkTearsheetGenerationFormContent({ onClose, onRefresh }: IBulkTearsheetGenerationFormProps) {
  const portfolioCompanies = useAtomValue(portfolioCompaniesAtom);
  const funds = useAtomValue(selectableFundsAtom);
  const { useTearsheetRendererV2 } = useFlags<MaggieFeatureFlags>();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = useMemo(
    () => bulkTearsheetGenerationSchema(portfolioCompanies, funds),
    [portfolioCompanies, funds]
  );

  const methods = useForm<BulkTearsheetGenerationPayload>({
    defaultValues: {
      companyIds: [],
      fundIds: [],
      asOfDate: Temporal.Now.plainDateISO().toString(),
    },
    resolver: yupResolver(schema),
  });

  const fieldsMap = useMemo(() => {
    const fields = schemaToFormFields<BulkTearsheetGenerationPayload>(schema);
    const form: IForm = {
      fields: FDMap.fromArray(fields, 'key'),
    };

    return form;
  }, [schema]);

  const handleSubmit = useCallback(async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      const values = methods.getValues();
      await generateTearsheets({ ...values, useRendererV2: useTearsheetRendererV2 });
      trackEvent('tearsheet.generated', { entity_type: 'tearsheet' });
      onClose();

      // Trigger refresh after 250ms to allow backend processing
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 250);
      }
    } catch (e) {
      setError('Failed to generate tearsheets, if the issue persists please contact support.');
    } finally {
      setIsSubmitting(false);
    }
  }, [methods, onClose, onRefresh, useTearsheetRendererV2]);

  return (
    <FormProvider {...methods}>
      <FormItemsOnlyFactory form={fieldsMap} />
      {error && (
        <Alert severity='error' sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <StickyFormButtons
        onCancel={onClose}
        onSubmit={methods.handleSubmit(handleSubmit)}
        loading={isSubmitting}
        style={{ padding: '0.5rem 0 2rem' }}
        submitLabel='Generate'
      />
    </FormProvider>
  );
}

export function BulkTearsheetGenerationForm({ onClose, onRefresh }: IBulkTearsheetGenerationFormProps) {
  return (
    <Suspense fallback={<Loader />}>
      <BulkTearsheetGenerationFormContent onClose={onClose} onRefresh={onRefresh} />
    </Suspense>
  );
}
